export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  image: string;
  imageAlt: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

export const services: ServiceData[] = [
  {
    slug: "fence-installation",
    title: "Professional Fence Installation",
    subtitle: "New wood, vinyl & privacy fences built to last",
    price: "Free Estimate",
    image: "/assets/images/professional-fence-installation.webp",
    imageAlt: "Newly installed white privacy fence bordering a landscaped Summit County garden",
    description:
      "From privacy and backyard enclosures to full-property lines, we install clean, straight, professionally set fences that hold up to Summit County's freeze-thaw cycles, wind, and heavy snow. Every job starts with proper layout, correctly set posts, and materials chosen for high-altitude durability.",
    features: [
      "Wood, cedar, vinyl & composite privacy fencing",
      "Properly set, frost-depth footings for mountain soil",
      "Straight, level runs on sloped and uneven lots",
      "Utility locates and property-line verification",
      "Gates and hardware installed to match",
      "Clean-up and haul-away when the job is done",
    ],
    process: [
      { step: "On-Site Estimate", desc: "We walk your property, take measurements, and give you a clear, free written estimate." },
      { step: "Layout & Materials", desc: "We confirm the line, mark utilities, and select materials built for high-country weather." },
      { step: "Professional Install", desc: "Posts are set to depth, panels squared and leveled, gates hung and adjusted." },
      { step: "Final Walkthrough", desc: "We review every run and gate with you and clean the site before we leave." },
    ],
  },
  {
    slug: "picket-fence-installation",
    title: "Picket Fence Installation",
    subtitle: "Classic wood pickets with timeless mountain curb appeal",
    price: "Free Estimate",
    image: "/assets/images/picket-fence-installation.webp",
    imageAlt: "Rustic wooden picket fence surrounding a garden bed",
    description:
      "Picket fencing adds classic charm and clean definition to yards, gardens, and front-of-home borders. We build wood picket fences (\"cercos de pique\") with even spacing, crisp lines, and finishes that weather beautifully in the Colorado high country.",
    features: [
      "Custom picket spacing, height, and cap styles",
      "Pressure-treated and cedar options",
      "Decorative post caps and matching gates",
      "Sealing and staining available",
      "Great for gardens, front yards, and pet areas",
      "Built to resist rot, wind, and snow load",
    ],
    process: [
      { step: "Design Consult", desc: "We help you choose picket style, height, and spacing to match your home." },
      { step: "Free Estimate", desc: "A clear, itemized quote with no pressure and no surprises." },
      { step: "Build & Set", desc: "Posts set true, pickets spaced evenly, gates hung and squared." },
      { step: "Finish & Review", desc: "Optional sealing, a full walkthrough, and a tidy site." },
    ],
  },
  {
    slug: "chain-link-fencing",
    title: "Chain Link Fencing",
    subtitle: "Durable, low-maintenance security & containment",
    price: "Free Estimate",
    image: "/assets/images/chain-link-fence-installation.webp",
    imageAlt: "Green coated metal mesh chain link fence with a locking gate",
    description:
      "Chain link is the workhorse of fencing — affordable, strong, and nearly maintenance-free. We install residential and commercial chain link for yards, kennels, storage lots, and property perimeters, with galvanized or color-coated options and gates sized to fit.",
    features: [
      "Galvanized and vinyl-coated (black/green) mesh",
      "Residential yards, pet runs, and dog kennels",
      "Commercial and storage-lot perimeters",
      "Walk-through and rolling drive gates",
      "Tension-wire and top-rail reinforcement",
      "Fast installs with minimal maintenance",
    ],
    process: [
      { step: "Site Assessment", desc: "We measure the run, note grade changes, and plan gate placement." },
      { step: "Free Estimate", desc: "Transparent pricing on materials, coating, and gates." },
      { step: "Install", desc: "Terminal and line posts set, mesh stretched tight, gates fitted." },
      { step: "Walkthrough", desc: "We test every gate and confirm a clean, secure finish." },
    ],
  },
  {
    slug: "construction-perimeter-fencing",
    title: "Construction & Perimeter Fencing",
    subtitle: "Secure site & property enclosure for builds and land",
    price: "Free Estimate",
    image: "/assets/images/construction-site-perimeter-fencing.webp",
    imageAlt: "Long perimeter fence line enclosing a rural property along a road",
    description:
      "Enclosing a job site or property line (\"rodear construcción\")? We install secure perimeter and construction-site fencing that keeps sites protected, marks boundaries, and controls access — for builders, developers, and property owners across Summit County.",
    features: [
      "Temporary and permanent perimeter fencing",
      "Construction-site security and access control",
      "Property-line and acreage enclosure",
      "Drive gates for equipment and vehicles",
      "Fast turnaround to keep projects on schedule",
      "Removal and relocation options available",
    ],
    process: [
      { step: "Scope Review", desc: "We assess the site, access needs, and how long the fence must stand." },
      { step: "Free Estimate", desc: "Clear pricing for install, gates, and any removal later." },
      { step: "Rapid Install", desc: "We enclose the perimeter quickly so your project can proceed." },
      { step: "Ongoing Support", desc: "Need it moved or removed later? We handle that too." },
    ],
  },
  {
    slug: "custom-aluminum-fencing",
    title: "Custom & Aluminum Fencing",
    subtitle: "Modern designs & ornamental metal that lasts",
    price: "Free Estimate",
    image: "/assets/images/custom-aluminum-fence-installation.webp",
    imageAlt: "Modern horizontal-slat aluminum fence in a dark charcoal finish",
    description:
      "For a refined, modern look with minimal upkeep, aluminum and custom-designed fencing is hard to beat. We install horizontal-slat, ornamental, and custom metal fences that resist rust and mountain weather while elevating your home's curb appeal.",
    features: [
      "Horizontal-slat and ornamental aluminum",
      "Powder-coated, rust-resistant finishes",
      "Custom heights, spacing, and gate designs",
      "Great for pool, patio, and front-yard borders",
      "Low-maintenance and long-lasting",
      "Design help to match your home's style",
    ],
    process: [
      { step: "Design Consult", desc: "We review styles, finishes, and layout to fit your vision." },
      { step: "Free Estimate", desc: "A detailed quote covering panels, posts, and custom gates." },
      { step: "Precision Install", desc: "Panels aligned and leveled for a crisp, modern finish." },
      { step: "Final Review", desc: "We walk the finished fence with you, edge to edge." },
    ],
  },
  {
    slug: "fence-repair-and-gates",
    title: "Fence Repair & Gate Installation",
    subtitle: "Fix leaning posts, damage & add or rebuild gates",
    price: "Free Estimate",
    image: "/assets/images/fence-repair-gate-installation.webp",
    imageAlt: "Cedar wood privacy fence with a gate and metal latch hardware",
    description:
      "Storms, heavy snow, and time take a toll on fences. We repair leaning posts, broken panels, and sagging gates — and install new gates and hardware — so your fence looks sharp and works the way it should, without a full replacement when one isn't needed.",
    features: [
      "Post resetting and leaning-fence correction",
      "Panel, picket, and rail replacement",
      "New walk-through and drive gate installation",
      "Latch, hinge, and hardware upgrades",
      "Storm and snow-load damage repair",
      "Honest advice: repair vs. replace",
    ],
    process: [
      { step: "Damage Assessment", desc: "We inspect the fence and tell you honestly what needs fixing." },
      { step: "Free Estimate", desc: "Clear pricing for repairs, gates, or partial replacement." },
      { step: "Repair & Install", desc: "Posts reset, panels replaced, gates hung and adjusted." },
      { step: "Walkthrough", desc: "We test gates and confirm the fence is solid before we go." },
    ],
  },
];
